import React, { useRef, useState, useEffect } from 'react';
import {
  AppBar,
  Button,
  Dialog,
  IconButton,
  Slide,
  Toolbar,
  Typography,
  Box,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import CloseIcon from '@mui/icons-material/Close';

import DishImagePreview from './DishImagePreview';
import DishVariantsSelector from './DishVariantsSelector';
import NoteField from './NoteField';
import AmountSelector from './AmountSelector';
import imageMap from '../../customComponents/imageMap'

const useStyles = makeStyles((theme) => ({
  appBar: { position: 'relative' },
  title: {
    marginLeft: theme.spacing(2) + ' !important',
    flex: 1,
  },
  dialogContent: {
    overflowY: 'auto',
    //paddingBottom: 80, // 👈 spazio per il bottone sticky
    '&::-webkit-scrollbar': { width: '5px' },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#43a047',
      borderRadius: '10px',
    },
  },
}));

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * PROPS
 * - dish                (required)
 * - open                (required)
 * - onClose             (required)
 * - onConfirm           (required)
 * - mode="add" | "edit" (default "add")
 *
 * - initialAmount
 * - initialVariants
 * - initialNote
 *
 */
export default function UserReservationDialog({
  dish,
  open,
  onClose,
  onConfirm,
  mode = "add",

  initialAmount = 1,
  initialVariants = [],
  initialNote = "",
}) {
  const classes = useStyles();
  const safeVariants = Array.isArray(dish?.variants) ? dish.variants : [];

  // -----------------------------
  // STATE (inizializzato da props → riutilizzabile)
  // -----------------------------
  const [amount, setAmount] = useState(initialAmount);
  const [variantsSelection, setVariantsSelection] = useState(initialVariants);
  const [variantErrors, setVariantErrors] = useState({});
  const [note, setNote] = useState(initialNote);

  // refs per la validazione del scrollToError
  const variantRefs = useRef({});

  // Reset automatico quando cambia dish o si riapre in modalità edit
  useEffect(() => {
    if (!open) return;
    setAmount(initialAmount);
    setVariantsSelection(initialVariants);
    setNote(initialNote);
    setVariantErrors({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, dish.id, initialAmount, initialNote]);

  // -----------------------------
  // IMAGE SRC
  // -----------------------------
  const dishImageSrc = dish.image
    ? `https://${process.env.REACT_APP_DOMAIN}/uploads/${dish.image}`
    : imageMap[dish.icon_key] || null;

  // -----------------------------
  // VALIDAZIONE VARIANTI
  // -----------------------------
  const validateVariants = () => {
    if (safeVariants.length === 0) return true;

    const missing = safeVariants.filter(v => {
      if (v.multi === 0) {
        const selected = variantsSelection.find(sel => sel.id === v.id);
        return !selected || !selected.selected?.length;
      }
      return false;
    });

    if (missing.length > 0) {
      const errMap = Object.fromEntries(missing.map(v => [v.id, true]));
      setVariantErrors(errMap);

      // scroll al primo errore
      variantRefs.current[missing[0].id]?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });

      return false;
    }

    setVariantErrors({});
    return true;
  };

  // -----------------------------
  // CONFIRM
  const handleConfirm = () => {
    if (!validateVariants()) return;

    // Mappiamo solo le varianti realmente selezionate
    const variantsForConfirm = safeVariants
      .map((v) => {
        const selected = variantsSelection.find(sel => sel.id === v.id)?.selected;
        if (!selected || selected.length === 0) return null; // niente selezione → scartiamo
        return {
          ...v,        // mantiene id, name, options, multi, position
          selected,    // aggiorna solo selected
        };
      })
      .filter(Boolean); // rimuove i null

    onConfirm({
      dish,
      amount,
      variants: variantsForConfirm,
      note,
      mode,
    });

    onClose();
  };

  // -----------------------------
  // RENDER COMPONENT
  // -----------------------------
  return (
    <Dialog
      fullScreen
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      PaperProps={{ className: classes.dialogContent }}
    >
      {/* HEADER */}
      <AppBar className={classes.appBar}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={onClose}>
            <CloseIcon />
          </IconButton>
          <Typography variant="h6" className={classes.title}>
            {mode === "edit" ? "Modifica selezione" : "Aggiungi al carrello"}
          </Typography>
        </Toolbar>
      </AppBar>

      {/* IMMAGINE + INFO PIATTO */}
      <DishImagePreview
        dishImageSrc={dishImageSrc}
        dishName={dish.name}
        dishPrice={dish.price}
        description={dish.description}
      />

      {/* VARIANTI */}
      {safeVariants.length > 0 && (
        <DishVariantsSelector
          dish={{ ...dish, variants: safeVariants }}
          variantsSelection={variantsSelection}
          setVariantsSelection={setVariantsSelection}
          variantErrors={variantErrors}
          setVariantErrors={setVariantErrors}
          variantRefs={variantRefs}
        />
      )}

      {/* NOTE */}
      <NoteField note={note} setNote={setNote} />

      {/* QUANTITÀ */}
      <AmountSelector
        amount={amount}
        increaseAmount={() => setAmount(a => a + 1)}
        decreaseAmount={() => setAmount(a => (a > 0 ? a - 1 : a))}
      />

      {/* BUTTON BOTTOM */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: '#000',
          px: 2,
          py: 1.5,
          boxShadow: '0 -4px 10px rgba(0,0,0,0.35)',
          zIndex: 1300,
        }}
      >
        <Button
          size="large"
          fullWidth
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          disabled={amount === 0}
          sx={{
            height: 48,
            minWidth: 240,
            px: 3,
            mx: 'auto',
            display: 'block',
            fontWeight: 'bold',
            fontSize: '0.95rem',
            borderRadius: 2,
            boxShadow: '0px -4px 10px rgba(0,0,0,0.35)',
            textTransform: 'none',
          }}
        >
          {mode === "edit" ? "Salva modifiche" : "Aggiungi al carrello"}
        </Button>
      </Box>
    </Dialog>
  );
}
